import React, { MutableRefObject, useRef } from "react";
import style from "./open-meet.module.scss";
import classnames from "classnames";
import { CookieConsent } from "@jmc/solid-design-system/src/utils/cookieConsent";

export interface Props {
    /** The OpenMeet video id */
    videoId: string;
    /** Command to toggle playing mode */
    play?: boolean;
    /** What happens when the video starts playing */
    onPlay?: (videoref: MutableRefObject<any> | null) => void;
    /** A Background image that is shown when the video is not playing yet */
    thumbnail?: string;
    /** SEO title */
    seoTitle?: string;
    /** Get cookie acceptance (Optanon) status */
    cookieAccept?: (accepted: boolean) => void;
    className?: string;
}

declare global {
    interface Window {
        OptanonActiveGroups: any;
    }
}

export const OpenMeet: React.FunctionComponent<Props> = ({
    videoId,
    play = false,
    thumbnail,
    seoTitle = null,
    cookieAccept = null,
    onPlay,
    className = null,
}: Props) => {
    const [playing] = React.useState(play);
    const videoref = useRef(null);
    const [iframeHeight, setIframeHeight] = React.useState(0);
    const name = `openMeetPlayer-${videoId}-${Math.random()}`;

    React.useEffect(() => {
        if (window?.OptanonActiveGroups?.split(",")?.includes(`${CookieConsent.STRICTLY_NECESSARY}`)) {
            cookieAccept && cookieAccept(true);
            thumbnail = null;
        }
        if (play) {
            onPlay && onPlay(null);
        }
    }, []);

    React.useEffect(() => {
        const clientWidth = videoref?.current.clientWidth;
        if (clientWidth > 0) {
            const aspectRatio = 16 / 9;
            setIframeHeight(Math.floor(clientWidth / aspectRatio));
        }
    }, [videoref?.current]);

    return (
        <div
            className={classnames([className, style["open-meet-player"], thumbnail && style["thumbnail"]])}
            style={{ height: iframeHeight, ...(thumbnail && !playing && { backgroundImage: `url(${thumbnail})` }) }}
            ref={videoref}
            data-test-id={`OpenMeet.Player.Wrapper.${videoId}`}
        >
            {playing && (
                <iframe
                    name={name}
                    data-test-id={`OpenMeet.Player.${videoId}`}
                    frameBorder="0"
                    allow="fullscreen"
                    src={videoId}
                    title={seoTitle}
                    style={{ height: iframeHeight, width: "100%" }}
                />
            )}
        </div>
    );
};

export default OpenMeet;
